import { useEffect, useState } from "react";
import { SEO } from "./SEO";
import { Reveal } from "./Reveal";

// Shared long-form legal layout: intro block + sticky section index (desktop)
// + numbered sections. Content comes verbatim from src/data/legal.js.
export const LegalLayout = ({ doc, path, seoTitle, seoDescription, testId }) => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      let current = 0;
      doc.sections.forEach((_, i) => {
        const el = document.getElementById(`legal-section-${i}`);
        if (el && el.getBoundingClientRect().top < 160) current = i;
      });
      setActive(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [doc.sections]);

  return (
    <>
      <SEO title={seoTitle} description={seoDescription} path={path} />
      <section className="pt-28 pb-16 sm:pt-36 sm:pb-20" data-testid={testId}>
        <div className="container-x">
          <Reveal className="max-w-3xl">
            <span className="eyebrow">{doc.eyebrow}</span>
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              {doc.title}
            </h1>
            {doc.introTitle && (
              <h2 className="mt-8 font-display text-lg font-bold text-slate-900">{doc.introTitle}</h2>
            )}
            {doc.intro.map((p, i) => (
              <p key={i} className="mt-4 text-sm leading-relaxed text-slate-600">
                {p}
              </p>
            ))}
            {doc.address && (
              <div className="card-line mt-6 p-5" data-testid="legal-registered-office">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-teal-700">
                  {doc.address.label}
                </span>
                {doc.address.lines.map((l) => (
                  <p key={l} className="mt-1 text-sm text-slate-700">
                    {l}
                  </p>
                ))}
              </div>
            )}
          </Reveal>

          <div className="mt-14 grid gap-10 lg:grid-cols-12">
            <aside className="hidden lg:col-span-4 lg:block xl:col-span-3">
              <nav
                className="sticky top-24 card-line max-h-[70vh] overflow-y-auto p-5"
                aria-label="Section index"
                data-testid="legal-section-index"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                  On this page
                </span>
                <ol className="mt-3 space-y-1">
                  {doc.sections.map((s, i) => (
                    <li key={s.title}>
                      <a
                        href={`#legal-section-${i}`}
                        className={`block rounded-lg px-2.5 py-1.5 text-xs leading-snug transition-colors ${
                          active === i
                            ? "bg-blue-50 font-semibold text-blue-700"
                            : "text-slate-500 hover:text-slate-900"
                        }`}
                        data-testid={`legal-index-${i}`}
                      >
                        {i + 1}. {s.title}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            </aside>

            <div className="lg:col-span-8 xl:col-span-8 xl:col-start-5">
              {doc.sections.map((s, i) => (
                <Reveal key={s.title} delay={Math.min(i * 0.02, 0.15)}>
                  <section
                    id={`legal-section-${i}`}
                    aria-labelledby={`legal-heading-${i}`}
                    className="mb-10 scroll-mt-24 border-b border-slate-100 pb-10 last:border-0"
                    data-testid={`legal-section-${i}`}
                  >
                    <h2
                      id={`legal-heading-${i}`}
                      className="font-display text-lg font-bold uppercase tracking-wide text-slate-900"
                    >
                      <span className="mr-2 font-mono text-sm font-medium text-blue-700">{i + 1}.</span>
                      {s.title}
                    </h2>
                    {s.paragraphs?.map((p, j) => (
                      <p key={j} className="mt-4 text-sm leading-relaxed text-slate-600">
                        {p}
                      </p>
                    ))}
                    {s.bullets && (
                      <ul className="mt-4 list-disc space-y-2 pl-5 text-sm leading-relaxed text-slate-600">
                        {s.bullets.map((b, j) => (
                          <li key={j}>{b}</li>
                        ))}
                      </ul>
                    )}
                    {s.closing?.map((p, j) => (
                      <p key={j} className="mt-4 text-sm leading-relaxed text-slate-600">
                        {p}
                      </p>
                    ))}
                  </section>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
